import {
  categoryIcons,
  SecurityStarIcon,
  ThumbnailCogIcon,
  SubsequentAddIcon,
  LightBulbIcon,
} from "../../services/master-data"; 
import FilePreviewCard from './FilePreview';
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/solid";
import {blobSignature} from "../../utils/general-config";

const FileThumbnail = ({ key ,file, fileUrl, onDeleteHandler }) => {
  const isBlobUrl = `${fileUrl}${blobSignature()}`;
  if(isBlobUrl)
    return (
      <div className="relative border-2 border-[#17AE06] rounded-md flex-shrink-0 overflow-hidden mx-1">
        <div className="w-full h-full bg-green-100 flex items-center justify-center text-[#17AE06] text-xs">
          <FilePreviewCard id={key} fileUrl={isBlobUrl} fileName={file.itemModifiedName} />
        </div>
        <button onClick={() => onDeleteHandler()} className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition flex items-center justify-center shadow-md">
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    );
};

// New component for the "N+ More" badge
const MoreBadge = ({ count }) => {
    return (
        <div 
            className="h-14 w-10 border border-gray-300 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 mr-2 
                       flex items-center justify-center text-xs font-semibold text-gray-600 cursor-default"
        >
            +{count}
        </div>
    );
};


const DocumentCategory = ({ category, uploadedFiles, onAddFile,onDeleteHandler }) => {
    const CategoryIcon = categoryIcons[category.id]; 
    const CategoryId = category.id;

    const maxPreviews = 3;
    const totalFiles = uploadedFiles.length;
    
    const filesToDisplay = uploadedFiles.slice(0, maxPreviews); 
    const hiddenFileCount = totalFiles - maxPreviews;
    const shouldShowBadge = hiddenFileCount > 0;

    const renderPreviews = () => {
        if (totalFiles === 0) return null;
        if (!shouldShowBadge || totalFiles <= maxPreviews) {
          return filesToDisplay.map((file) => {
              const isImage = file?.categoryName?.startsWith("image");
              const itemPreviewURL = file?.itemPreviewURL;
              if (isImage && itemPreviewURL === "") {
                return <FileThumbnail key={file.id} file={file} fileUrl={file.itemPath} onDeleteHandler={() => onDeleteHandler(CategoryId,file)} />;
              } else {
                return <FileThumbnail key={file.id} file={file} fileUrl={file.itemPreviewURL} onDeleteHandler={() => onDeleteHandler(CategoryId,file)} />;
              }
            });
        }
        
        const visiblePreviews = filesToDisplay.slice(0, maxPreviews - 1);
        const previewElements = visiblePreviews.map((file) => {
          const isImage = file?.categoryName?.startsWith("image");
          const itemPreviewURL = file?.itemPreviewURL;
          if (isImage && itemPreviewURL === "") {
            return <FileThumbnail key={file.id} file={file} fileUrl={file.itemPath} onDeleteHandler={() => onDeleteHandler(CategoryId,file)} />;
          } else {
            return <FileThumbnail key={file.id} file={file} fileUrl={file.itemPreviewURL} onDeleteHandler={() => onDeleteHandler(CategoryId,file)} />;
          }
        });
        
        previewElements.push(<MoreBadge key="more-badge" count={hiddenFileCount} />);

        return previewElements;
    };

    const isInitialAddButton = totalFiles === 0;
    return (
      <div className="p-3 sm:p-4 bg-white rounded-[5px] shadow hover:shadow-sm transition duration-200 !mt-0 !mb-4">
        {/* 🚨 KEY CHANGE: Flex container with mobile stacking 🚨 */}
        <div
          className={`flex ${
            isInitialAddButton ? "flex-row" : "flex-col sm:flex-row"
          } sm:items-center sm:justify-between`}
        >
          {/* 1. Category Icon and Text (Left side on Desktop, Top on Mobile) */}
          <div className="flex items-center flex-grow sm:flex-shrink-0">
            {/* Category Icon */}
            <div className="flex items-center justify-center p-[3px] w-[35px] h-[35px] rounded-[8.5px] bg-indigo-50 mr-3 sm:mr-4 flex-shrink-0">
              <div className="h-5 w-5 text-indigo-600 flex items-center justify-center">{CategoryIcon}</div>
            </div>

            {/* Category Text */}
            <div className="flex-grow min-w-0 pr-2">
              <div className="flex items-center">
                <h3 className="text-xs md:text-lg font-bold text-[#16283D]">{category?.title}</h3>

                {/* Security Star Icon */}
                {category?.sensitive && (
                  <div className="h-6 w-6 flex items-center justify-center ml-1 sm:ml-2 bg-[#E7F1FF] rounded-full flex-shrink-0">
                    {LightBulbIcon}
                  </div>
                )}
              </div>
              <p className="text-[10px] md:text-xs text-gray-500 truncate">{category?.description}</p>
            </div>
          </div>

          {/* 2. Files and Add Button Area (Right side on Desktop, Bottom on Mobile) */}
          {/* Hidden only if initial button is shown on mobile */}
          <div
            className={`
                    items-center space-x-2 mt-3 pt-3 border-t border-gray-100 sm:mt-0 sm:pt-0 sm:border-none 
                    ${isInitialAddButton && totalFiles === 0 ? "hidden sm:flex" : "flex"} 
                `}
          >
            {/* File Previews Container - Added flex-shrink-0 to prevent button crowding */}
            <div className="flex items-center overflow-x-auto flex-shrink-0">{renderPreviews()}</div>

            {/* Add File Button/Icon - Always visible when files exist, or on desktop when files don't exist */}
            {isInitialAddButton ? (
              <button
                onClick={() => onAddFile(category)}
                className="flex-shrink-0 rounded-full font-medium text-sm transition duration-150 h-10 px-4 bg-[#0E77FF] hover:bg-[#085dcb] text-white flex items-center"
              >
                <span className="whitespace-nowrap">+ Add file</span>
              </button>
            ) : (
              <button
                onClick={() => onAddFile(category)}
                className="flex-shrink-0 rounded-[6px] w-[58px] h-[80px] font-medium text-sm transition duration-150 border-2 border-[#0E77FF] hover:bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <PlusCircleIcon className="w-[25px] h-[25px] text-[#0E77FF]" />
              </button>
            )}
          </div>

          {/* 3. Dedicated Full-Width Add Button for Mobile (when files are empty) */}
          {isInitialAddButton && (
            <div className="mt-3 pt-3 border-t border-gray-100 sm:hidden">
              <button
                onClick={() => onAddFile(category)}
                className="hidden md:block w-full h-10 px-4 bg-[#0E77FF] hover:bg-[#085dcb] text-white flex items-center justify-center rounded-xl font-medium text-sm transition duration-150"
              >
                <span className="whitespace-nowrap">+ Add file</span>
              </button>
              <button
                onClick={() => onAddFile(category)}
                className="md:hidden w-full h-10 px-4 text-white flex items-center justify-center rounded-xl font-medium text-sm transition duration-150 mt-[-24px]"
              >
                <PlusCircleIcon className="w-[25px] h-[25px] text-[#0E77FF]" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
};

export default DocumentCategory;